import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    FaHome,
    FaBox,
    FaUndo,
    FaTag,
    FaExclamationCircle,
    FaClipboardList,
    FaCloudUploadAlt,
    FaImages,
    FaStar,
    FaRupeeSign,
    FaWarehouse,
    FaBullhorn,
    FaBell,
    FaHeadset,
    FaChevronDown,
    FaChevronUp,
    FaCog,
    FaPowerOff
} from 'react-icons/fa';
import { useSeller } from '../../context/SellerContext';

const SellerSidebar = () => {
    const location = useLocation();
    const { logout } = useSeller();
    const [isPricingOpen, setIsPricingOpen] = useState(false);
    const [isProfileOpen, setIsProfileOpen] = useState(false);

    const isActive = (path) => location.pathname === path;

    const menuItems = [
        { name: 'Home', path: '/seller/dashboard', icon: <FaHome /> },
        { name: 'Orders', path: '/seller/orders', icon: <FaBox /> },
        { name: 'Returns', path: '/seller/returns', icon: <FaUndo /> },
        {
            name: 'Pricing',
            path: '#',
            icon: <FaTag />,
            hasSubmenu: true,
            isOpen: isPricingOpen,
            toggle: () => setIsPricingOpen(!isPricingOpen),
            submenu: [
                { name: 'Price Recommendation', path: '/seller/pricing/recommendation' },
                { name: 'Price Calculator', path: '/seller/pricing/calculator' }
            ]
        },
        { name: 'Claims', path: '/seller/claims', icon: <FaExclamationCircle /> },
        { name: 'Inventory', path: '/seller/inventory', icon: <FaClipboardList /> },
        { name: 'Catalog Uploads', path: '/seller/catalog-upload', icon: <FaCloudUploadAlt /> },
        { name: 'Image Bulk Upload', path: '/seller/image-upload', icon: <FaImages /> },
        { name: 'Quality', path: '/seller/quality', icon: <FaStar /> },
        { name: 'Payments', path: '/seller/payments', icon: <FaRupeeSign /> },
        { name: 'Warehouse', path: '/seller/warehouse', icon: <FaWarehouse /> },
    ];

    return (
        <div className="w-64 bg-[#1e1e1e] text-gray-300 h-screen fixed left-0 top-0 overflow-y-auto flex flex-col z-50 transition-all duration-300">
            {/* Header Profile Section */}
            <div className="relative">
                <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#2d2d2d] border-b border-gray-700"
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                >
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                            IS
                        </div>
                        <span className="font-semibold text-white">Insta Seller</span>
                    </div>
                    <FaChevronDown className={`transform transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                </div>

                {/* Profile Dropdown */}
                {isProfileOpen && (
                    <div className="absolute top-full left-0 w-full bg-white text-gray-800 shadow-lg py-2 z-50 rounded-b-lg">
                        <Link to="/seller/settings" className="flex items-center gap-3 px-4 py-2 hover:bg-gray-100">
                            <FaCog /> Settings
                        </Link>
                        <button
                            onClick={logout}
                            className="w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 text-red-600"
                        >
                            <FaPowerOff /> Logout
                        </button>
                    </div>
                )}
            </div>

            {/* Top Actions */}
            <div className="flex border-b border-gray-700">
                <Link to="/seller/notices" className="flex-1 p-3 flex items-center justify-center gap-2 hover:bg-[#2d2d2d] border-r border-gray-700 text-sm">
                    <FaBell /> Notices
                </Link>
                <Link to="/seller/support" className="flex-1 p-3 flex items-center justify-center gap-2 hover:bg-[#2d2d2d] text-sm">
                    <FaHeadset /> Support
                </Link>
            </div>

            {/* Menu Items */}
            <div className="py-4 flex-1">
                <div className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Manage Business
                </div>

                <nav className="space-y-1">
                    {menuItems.map((item, index) => (
                        <div key={index}>
                            {item.hasSubmenu ? (
                                <div>
                                    <button
                                        onClick={item.toggle}
                                        className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium transition-colors ${item.isOpen ? 'bg-[#2d2d2d] text-white' : 'hover:bg-[#2d2d2d] hover:text-white'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg">{item.icon}</span>
                                            {item.name}
                                        </div>
                                        {item.isOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                    </button>

                                    {item.isOpen && (
                                        <div className="bg-[#111] py-1">
                                            {item.submenu.map((subItem, subIndex) => (
                                                <Link
                                                    key={subIndex}
                                                    to={subItem.path}
                                                    className={`block pl-12 pr-4 py-2 text-sm hover:text-white ${isActive(subItem.path) ? 'text-pink-500' : 'text-gray-400'
                                                        }`}
                                                >
                                                    {subItem.name}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    to={item.path}
                                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${isActive(item.path)
                                        ? 'bg-[#2d2d2d] text-white border-l-4 border-pink-500'
                                        : 'hover:bg-[#2d2d2d] hover:text-white border-l-4 border-transparent'
                                        }`}
                                >
                                    <span className="text-lg">{item.icon}</span>
                                    {item.name}
                                </Link>
                            )}
                        </div>
                    ))}
                </nav>

                <div className="mt-6 px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Boost Sales
                </div>
                <nav className="space-y-1">
                    <Link
                        to="/seller/influencer-marketing"
                        className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${isActive('/seller/influencer-marketing')
                            ? 'bg-[#2d2d2d] text-white border-l-4 border-pink-500'
                            : 'hover:bg-[#2d2d2d] hover:text-white border-l-4 border-transparent'
                            }`}
                    >
                        <span className="text-lg"><FaBullhorn /></span>
                        Promotional Marketing
                    </Link>
                </nav>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 bg-[#111]">
                <div className="flex items-center gap-2 text-gray-400">
                    <span className="font-bold text-lg text-gray-200">Kalinga</span>
                    <span className="text-xs">Supplier Hub</span>
                </div>
            </div>
        </div>
    );
};

export default SellerSidebar;
